import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from '../../../../hooks/useSelector'
import { Card as SemCard, Segment } from 'semantic-ui-react'
import { TurnMessage } from '@prometheus/types'
import { SemanticCOLORS } from 'semantic-ui-react/dist/commonjs/generic'
import classNames from 'classnames'
import { isEqual } from 'lodash'
import { Button, Card, Typography, TypographyColor } from '@prometheus/components'
import ErrorHandler from '@/components/ErrorHandler'
import { useDispatch } from 'react-redux'
import { clearMessages } from '@/reducers/game/game'
import { usePreviousValue } from '@/hooks/usePreviousValue'

interface PlayerTurnMessageItemProps {
  message: TurnMessage
  onClear: (message: TurnMessage) => void
}

const PlayerTurnMessageItem = ({ message, onClear }: PlayerTurnMessageItemProps) => {
  let color: TypographyColor | undefined = undefined
  switch (message.type) {
    case 'error':
      color = 'danger'
      break
    case 'info':
      color = 'secondary'
      break
    case 'success':
      color = 'primary'
      break
    case 'warn':
      color = 'warning'
      break
    default: break
  }

  if (message.urgent) {
    return (
      <li onClick={() => onClear(message)}>
        <Typography variant="h2" color={color}>
          {message.message}
        </Typography>
      </li>
    )
  }

  return (
    <li onClick={() => onClear(message)}>
      <Typography variant="h4" color={color} style={{ whiteSpace: 'pre-wrap' }}>
        {message.message}
      </Typography>
    </li>
  )
}

export default () => {
  const tm = useSelector(s => s.game.sheet?.turn_messages) || []
  const sm = useSelector(s => s.game.metadata?.shared_messages) || []
  const errors = useSelector(s => s.game.errors) ?? []
  const is_map = useSelector(s => s.game.galaxy_display)
  const [turn_messages, set_turn_messages] = useState(tm)
  const [shared_messages, set_shared_messages] = useState(sm)
  const dispatch = useDispatch()

  const clearAll = useCallback(() => {
    dispatch(clearMessages())
    set_turn_messages([])
    set_shared_messages([])
  }, [])

  const ptm = usePreviousValue(tm)
  const psm = usePreviousValue(sm)

  useEffect(() => {
    if (!isEqual(ptm, tm)) {
      set_turn_messages(tm)
    }
  }, [ptm, tm])

  useEffect(() => {
    if (!isEqual(psm, sm)) {
      set_shared_messages(sm)
    }
  }, [psm, sm])

  const onClear = useCallback((message: TurnMessage) => {
    const tmIndex = turn_messages.indexOf(message)
    if (tmIndex !== -1) {
      set_turn_messages(m => {
        const next = [...m];
        next.splice(tmIndex, 1)
        return next;
      })
    }

    const smIndex = shared_messages.indexOf(message)
    if (smIndex !== -1) {
      set_shared_messages(m => {
        const next = [...m];
        next.splice(smIndex, 1)
        return next;
      })
    }
  }, [turn_messages, shared_messages])

  const hidden = !turn_messages.length && !shared_messages.length && !errors.length

  const messages = [...shared_messages, ...turn_messages].sort((a, b) => {
    if (a.urgent && !b.urgent) return -1
    if (!a.urgent && b.urgent) return 1
    return 0
  })

  return (
    <Card transparent style={{ display: hidden ? 'none' : 'block', position: 'relative', zIndex: 1, borderTopRightRadius: '0', borderBottomRightRadius: '0', borderRight: 'none' }} className={classNames({ is_map })} padding="sm">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h4" color="primary" retro>MESSAGES</Typography>
        <Button variant="warning" size="sm" onClick={clearAll}>Clear All</Button>
      </div>
      <ErrorHandler />
      {!!messages.length && (
        <ul style={{ paddingLeft: 'var(--prometheus-spacing-lg)'}}>
          {messages.map(message => (
            <PlayerTurnMessageItem key={message.message} message={message} onClear={onClear} />
          ))}
        </ul>
      )}
    </Card>
  )
}
